import jwt_decode from 'jwt-decode';

import { loadUserPermissions } from '../Components/getUserBranchPermissions';
import { newUiUrl } from '../config';

// Login hands the user over to the New UI instead of this app's own dashboard.
//
// The New UI is served from a different origin and keeps its session in
// httpOnly cookies, so nothing this app wrote to localStorage — token,
// refreshToken, permissions — travels with the navigation, and a bare redirect
// would land the user on the New UI's *login* screen.
//
// The tokens are POSTed to the New UI's hand-off endpoint instead
// (app/api/auth/handoff over there), which mints the cookie session
// server-side and 303s to its dashboard.
//
// Why a form POST and not a URL, or a fetch():
//   - Query params would put the tokens in the URL bar, browser history, the
//     Referer of the next outbound request and every access log in between.
//     The refreshToken is good for 30 days there, so that is a durable account
//     takeover rather than a transient leak.
//   - A cross-origin fetch() can't do it either: the Set-Cookie would be a
//     third-party cookie write and browsers drop those. A form POST is a
//     top-level navigation, so the cookie is first-party at the destination.
//
// The New UI only accepts the hand-off from allow-listed origins
// (LEGACY_APP_ORIGINS on that side), so this app's origin has to be on the list
// for whichever environment it is deployed to.
export const NEW_UI_URL = newUiUrl;
export const NEW_UI_HANDOFF_PATH = '/api/auth/handoff';

export function buildHandoffUrl() {
  return `${NEW_UI_URL.replace(/\/+$/, '')}${NEW_UI_HANDOFF_PATH}`;
}

// Field names mirror the login response (`accessToken`/`refreshToken`) so both
// sides read from the same contract.
export function buildHandoffForm(payload) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = buildHandoffUrl();
  form.style.display = 'none';

  const addField = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  addField('accessToken', (payload && payload.accessToken) || '');
  addField('refreshToken', (payload && payload.refreshToken) || '');

  return form;
}

// Submitting navigates the tab, so this does not return. Note it leaves the
// login page in history: a form POST cannot replace the current entry the way
// window.location.replace could, so Back lands on the login form again.
export function redirectToNewUi(payload) {
  const form = buildHandoffForm(payload);
  document.body.appendChild(form);
  markHandoffAttempt();
  form.submit();
}

// ---------------------------------------------------------------------------
// Returning to this app once the New UI owns the session
// ---------------------------------------------------------------------------
//
// Login stores the full legacy session (token, refreshToken, decoded claims and
// permissions) before handing off, so re-opening this app finds a usable
// session rather than a login form. We hand that session straight back to the
// New UI instead of rendering the legacy app.
//
// The same form POST is reused rather than a bare navigation: a plain redirect
// works only while the New UI's own cookies are still alive, whereas posting
// the tokens re-mints the session in every case (new browser profile, cleared
// cookies, expired session cookie).

// Escape hatch. The New UI's header has an "old UI" switch that opens this app
// on purpose — without this it would bounce straight back and the switch would
// be unusable. Anything opening this app deliberately appends ?stay=1.
export const STAY_PARAM = 'stay';

// ---------------------------------------------------------------------------
// Getting rid of a legacy session the New UI no longer honours
// ---------------------------------------------------------------------------
//
// The two apps store their sessions in places neither can reach: ours is
// localStorage on this origin, the New UI's is cookies on its own. So when the
// New UI's session ends — signed out, expired, refresh token revoked server
// side because the account signed in elsewhere — OUR copy of the tokens is left
// behind, and it still looks fine to hasHandbackSession(): `exp` is only read
// out of the access token locally and says nothing about whether the server
// still honours the refresh token.
//
// The result is the reported dead end. Opening this app hands the dead tokens
// back, the New UI cannot mint a session from them and shows "session expired",
// and returning here hands them back again. Nothing ever clears them, so the
// user cannot reach a login form in either app.
//
// Two ways out, deliberately independent of each other:

// Where a browser with no session belongs. Not imported from the router: this
// runs before React — and therefore before any route table — exists.
const LOGIN_PATH = '/login';

// 1. The New UI tells us. It appends ?signedout=1 when it ends a session or
//    rejects a hand-off, and we bin our copy before rendering the login. This
//    is the clean path, but it only works once the New UI sends the parameter.
export const SIGNED_OUT_PARAM = 'signedout';

// 2. We work it out ourselves, so a browser stuck in the loop right now
//    recovers without waiting on a New UI deploy. Handing off records the
//    attempt; coming back this soon afterwards means the New UI would not take
//    the tokens, because a hand-off that worked leaves the user over there.
//    sessionStorage is per-tab and per-origin, so the mark survives the round
//    trip in the tab that made it and cannot leak into another tab.
const HANDOFF_MARK_KEY = 'ezyNewUiHandoffAt';
const HANDOFF_BOUNCE_MS = 30 * 1000;

function markHandoffAttempt() {
  try {
    sessionStorage.setItem(HANDOFF_MARK_KEY, String(Date.now()));
  } catch (err) {
    // Private mode / storage full — we simply lose the loop guard.
  }
}

function clearHandoffMark() {
  try {
    sessionStorage.removeItem(HANDOFF_MARK_KEY);
  } catch (err) {}
}

// True when we handed off moments ago and are already back — i.e. the New UI
// bounced us, so the stored tokens are dead however healthy they parse.
function bouncedBackFromNewUi() {
  try {
    const at = parseInt(sessionStorage.getItem(HANDOFF_MARK_KEY), 10);
    if (!at) return false;
    return Date.now() - at < HANDOFF_BOUNCE_MS;
  } catch (err) {
    return false;
  }
}

// A tab that is on this app ON PURPOSE — opened by the New UI's "old UI" switch
// (?stay=1) or handed a session by it. Without this, the hand-back below fires
// on the very next full load: the switch drops the query string as soon as the
// SPA routes, so a refresh on /dashboardBI would throw the user back to the New
// UI and the old UI would be unusable past its first paint.
//
// sessionStorage, so it is scoped to this tab and gone when the tab closes:
// another tab re-entering the legacy app still hands back as designed.
const STAY_ON_LEGACY_KEY = 'ezyStayOnLegacy';

function markStayOnLegacy() {
  try {
    sessionStorage.setItem(STAY_ON_LEGACY_KEY, '1');
  } catch (err) {}
}

function stayingOnLegacyTab() {
  try {
    return sessionStorage.getItem(STAY_ON_LEGACY_KEY) === '1';
  } catch (err) {
    return false;
  }
}

// Drop this app's whole session. Same pair of calls the Logout button makes
// (HeaderBar onLogout), so a session ended in the New UI leaves exactly the
// state a legacy logout would.
export function clearLegacySession() {
  try {
    localStorage.clear();
  } catch (err) {}
  try {
    sessionStorage.clear();
  } catch (err) {}
}

// Paths that must keep rendering in THIS app even when a live session exists.
// Everything else hands back, so re-entering the legacy app anywhere — a
// bookmark, a restored tab, /dashboardBI — lands on the New UI.
//
// Two kinds of exception, both load-bearing:
//   - public pages reached WITHOUT a session (shared questionnaire links,
//     sign-up, password reset, the other login screens). Bouncing these would
//     break the link for the person following it.
//   - OAuth return targets, which are hit by an ALREADY signed-in user coming
//     back from Xero/Outlook/Google/HubSpot (see the redirect* URLs in
//     src/config.js). These look like ordinary authed routes, so they would
//     hand back and break the integration half-way through connecting.
const STAY_ON_LEGACY_PATHS = [
  // Public / pre-auth (App.js routes these outside the authed shell).
  '/forgot-password',
  '/admin-login',
  '/login/supervisor',
  '/c_q',
  '/home/privacypolicy',
  '/terms-and-conditions',
  '/user-signup',
  // OAuth / integration return targets.
  '/xero-sync',
  '/xero-sync-add-account',
  '/xero-sync-invoices',
  '/outlook-integration',
  '/graph-integration',
  '/account-setting-gmail',
  '/account-setting-hubspot',
  '/document-sync',
];

// Compared case-insensitively and ignoring a trailing slash, and a match also
// covers everything below it (`/forgot-password/:token`, the xero sub-routes).
// Note `/login` is deliberately NOT here — that one SHOULD hand back — while
// `/login/supervisor` is, and being the more specific entry it still wins.
export function staysOnLegacy(pathname) {
  const path = String(pathname || '/')
    .toLowerCase()
    .replace(/\/+$/, '');
  if (!path) return false; // '/' — the login landing, hands back
  return STAY_ON_LEGACY_PATHS.some((p) => path === p || path.indexOf(p + '/') === 0);
}

// Same path, with the deliberate-visit flag put back on. `clearLegacySession()`
// wipes sessionStorage, so the query string is what carries the intent across a
// reload that clears the marker.
function stayUrlFor(pathname) {
  return (pathname || '/') + '?' + STAY_PARAM + '=1';
}

// Milliseconds of clock skew tolerated when reading `exp`, matching the New
// UI's own check (lib/auth/jwt.ts isExpired).
const EXPIRY_SKEW_MS = 30 * 1000;

export function isTokenUsable(token) {
  if (!token) return false;
  try {
    const claims = jwt_decode(token);
    if (!claims || !claims.sub) return false;
    // A token with no `exp` never expires, matching the New UI's reading.
    if (!claims.exp) return true;
    return claims.exp * 1000 > Date.now() + EXPIRY_SKEW_MS;
  } catch (err) {
    return false;
  }
}

// A session this app may hand back. Deliberately narrow:
//   - `admintoken` means a platform-admin session, which lives in the legacy
//     admin area and has no New UI equivalent to hand to.
//   - supervisors keep their own legacy screens.
//   - both tokens are required; the New UI needs the refresh token to keep the
//     session alive past the access token's lifetime.
export function hasHandbackSession() {
  if (localStorage.getItem('admintoken')) return false;
  if (localStorage.getItem('isSupervisor') === 'True') return false;
  if (!localStorage.getItem('refreshToken')) return false;
  return isTokenUsable(localStorage.getItem('token'));
}

// Called once at start-up, before React renders, so no legacy screen flashes.
// Returns true when it has started navigating away — the caller then skips
// rendering entirely.
export function maybeHandBackToNewUi() {
  try {
    const params = new URLSearchParams(window.location.search);

    // Arrived on purpose (the New UI's "old UI" switch). Stay, and forget any
    // hand-off mark so the guard below cannot misread a deliberate visit as a
    // bounce.
    if (params.get(STAY_PARAM)) {
      clearHandoffMark();
      markStayOnLegacy();
      return false;
    }

    // The New UI has ended the session and told us so. Bin our copy, otherwise
    // it sits here looking valid and gets handed back forever.
    //
    // Then go to the login route explicitly instead of clearing and letting the
    // render fall through to App.js's <Redirect to='/login'>. That guard is only
    // consulted once React has rendered, and ?signedout=1 lands on '/' — the one
    // path getThemeLayout() rewrites to a dashboard route on the way past. A
    // fresh boot on /login skips that whole sequence, so the login form is what
    // renders no matter what any dashboard component does at construction time.
    //
    // location.replace, for the same reason listenForNewUiSession() reloads
    // rather than re-rendering: Redux is seeded from localStorage at boot, so
    // only a fresh boot reflects the session we just cleared. A replace() also
    // keeps the ?signedout=1 URL out of history, so Back cannot return to it.
    //
    // This cannot loop. The reload carries no ?signedout=1, and
    // clearLegacySession() has just made hasHandbackSession() false, so the next
    // pass through this function falls straight through and renders the login.
    if (params.get(SIGNED_OUT_PARAM)) {
      clearLegacySession();
      window.location.replace(LOGIN_PATH);
      return true;
    }

    // Already established that this tab is here deliberately (?stay=1 arrived on
    // an earlier load, or the New UI handed a session in). The query string is
    // long gone by then — App.js pushState()s to /dashboardBI on first render.
    if (stayingOnLegacyTab()) return false;

    if (staysOnLegacy(window.location.pathname)) return false;

    // We just handed off and we are already back, so the New UI refused these
    // tokens. Clear them and render the login — handing them back again is the
    // loop the user cannot escape.
    if (bouncedBackFromNewUi()) {
      clearLegacySession();
      return false;
    }

    if (!hasHandbackSession()) return false;

    redirectToNewUi({
      accessToken: localStorage.getItem('token'),
      refreshToken: localStorage.getItem('refreshToken'),
    });
    return true;
  } catch (err) {
    // Never let this keep the app from starting — worst case the user sees the
    // legacy login and signs in again.
    return false;
  }
}

// ---------------------------------------------------------------------------
// Receiving a session FROM the New UI ("Switch to Old UI")
// ---------------------------------------------------------------------------
//
// The New UI's header has an "old UI" switch. It opens this app with ?stay=1,
// which stops the hand-back above — but carries no session, so a user who
// signed in on the New UI's OWN login form arrived here to a login screen.
// (Signing in through THIS app left our localStorage populated, which is why
// the switch appeared to work at all.)
//
// The reverse of the login hand-off is not available to us: that is a form POST
// to the New UI's /api/auth/handoff, and this app is a static CRA bundle with
// no server to read a request body. So the New UI postMessages the tokens into
// the window it opened, the same way SideBar.js hands a token to the Custom
// Dashboard. Names are mirrored in the New UI's lib/legacy-app.ts — change them
// in both repos or the switch silently stops carrying the session.
export const SESSION_MESSAGE = 'EZY_SESSION';
export const SESSION_ACK_MESSAGE = 'EZY_SESSION_RECEIVED';

// Origin of the New UI, derived from the same config the hand-off posts to, so
// one setting governs both directions.
function newUiOrigin() {
  try {
    return new URL(NEW_UI_URL).origin;
  } catch (err) {
    return null;
  }
}

// Mirrors what Login.js writes on a normal sign-in, so the app that renders
// afterwards cannot tell the difference between this and a local login.
function storeHandedOverSession(accessToken, refreshToken) {
  const claims = jwt_decode(accessToken);
  localStorage.setItem('token', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('userId', claims.sub);
  localStorage.setItem('userType', claims.UserType);
  localStorage.setItem('userEmail', claims.email);
  localStorage.setItem('userOwner', claims.IsOwner);
  localStorage.setItem('isAgent', claims.IsAgent);
  localStorage.setItem('selectedBranchId', claims.BranchId);
  localStorage.setItem('companyId', claims.Com);
  localStorage.setItem('userName', claims.FullName);
}

// Installed at start-up, BEFORE React renders, because the New UI starts posting
// as soon as the window opens and a listener added later would miss the first
// sends. It keeps retrying for 30s, so a slow bundle still lands, but there is
// no reason to burn those retries.
//
// Reloading rather than re-rendering: the whole app reads localStorage through
// Redux state seeded at boot, so nothing short of a fresh boot picks the
// session up. `location.replace` keeps the ?stay=1 URL out of history.
export function listenForNewUiSession() {
  const origin = newUiOrigin();
  if (!origin) return;

  // The New UI posts once a second for 30s, so a second message can land while
  // the permission call below is still in flight. Handle exactly one.
  let handled = false;

  const onMessage = function (event) {
    if (event.origin !== origin) return; // only ever the configured New UI
    const data = event.data;
    if (!data || data.type !== SESSION_MESSAGE) return;
    if (!isTokenUsable(data.accessToken) || !data.refreshToken) return;
    if (handled) return;

    try {
      storeHandedOverSession(data.accessToken, data.refreshToken);
    } catch (err) {
      return; // undecodable token — leave the login form up, it is recoverable
    }
    handled = true;

    // Ack first: the New UI stops resending on this, and a reload would tear
    // the listener down before it ever went out.
    try {
      if (event.source) event.source.postMessage({ type: SESSION_ACK_MESSAGE }, origin);
    } catch (err) {}

    window.removeEventListener('message', onMessage);
    // This tab is here on purpose — remember it before the reload, so the
    // hand-back does not fire the moment the session exists.
    markStayOnLegacy();

    // The tokens alone are not a legacy session. App.js (componentWillMount)
    // clears the WHOLE of localStorage on boot when `userBranchPermissions` is
    // missing while `selectedBranchId` is set — which is exactly the state
    // storeHandedOverSession leaves behind — so the reload below would land on
    // the login form with the handed-over session wiped. Login has the same
    // requirement and calls loadUserPermissions() for it (see
    // finishLoginAndHandOff); this is the receiving end of the same contract.
    loadUserPermissions()
      .then(function () {
        // Keep ?stay=1: this reload is a bare load of '/' as far as
        // maybeHandBackToNewUi is concerned, and a live session with no flag is
        // precisely what it hands straight back to the New UI.
        window.location.replace(stayUrlFor(window.location.pathname));
      })
      .catch(function () {
        // No permissions, no usable legacy session — App.js would bin it on the
        // next boot anyway. Clear it ourselves and render the login form, which
        // the user can recover from, rather than a half-session that cannot.
        clearLegacySession();
        window.location.replace(stayUrlFor(window.location.pathname));
      });
  };

  window.addEventListener('message', onMessage);
}

export default redirectToNewUi;
